import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Link } from '@tanstack/react-router';
import { ArrowUpRightIcon } from 'lucide-react';

import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getEstate } from '@/features/estates-property/api/estate';
import { getProject } from '@/features/projects/api/projects';
import useResizeObserver from '@/hooks/use-resize-observer';
import { cn } from '@/lib/utils';

import { CollapsableCard } from './collapsable-card';
import { ObjectRelationSettingsContent } from './object-relation-settings-content';

interface ObjectRelationSettingsProps {
  onChangeEstateId: React.Dispatch<React.SetStateAction<string | undefined>>;
  onChangeProjectId: React.Dispatch<React.SetStateAction<string | undefined>>;
  disableSelectObject: boolean;
  estateId?: string;
  projectId?: string;
}

export const ObjectRelationSettings = ({
  onChangeEstateId,
  onChangeProjectId,
  disableSelectObject,
  estateId,
  projectId,
}: ObjectRelationSettingsProps) => {
  const { t } = useTranslation();
  const isMobileView = !useResizeObserver(640);

  const [isOpened, setIsOpened] = useState(false);
  const [selectedObject, setSelectedObject] = useState<{
    name?: string | null;
    address?: {
      address?: string | null;
    } | null;
  }>();

  const handleOpenChange = (open: boolean) => {
    if (!disableSelectObject) {
      setIsOpened(open);
    }
  };

  const fetchAndSetObject = useCallback(async () => {
    if (estateId && disableSelectObject) {
      getEstate(estateId).then((estate) => {
        setSelectedObject({
          ...estate,
          address: {
            address: estate.address?.address || null,
          },
        });
      });
    } else if (projectId && disableSelectObject) {
      getProject(projectId).then((project) => {
        setSelectedObject({
          ...project,
        });
      });
    }
  }, [estateId, projectId, disableSelectObject]);

  useEffect(() => {
    fetchAndSetObject();
  }, [fetchAndSetObject]);

  return (
    <CollapsableCard title={t('object')}>
      <div className="relative flex items-center">
        <Popover onOpenChange={handleOpenChange} open={isOpened}>
          <PopoverTrigger asChild>
            <div
              className={cn(
                'flex min-h-[40px] w-full items-center justify-between gap-x-4 rounded-sm px-4 py-2 sm:min-h-[26px] sm:px-2 sm:py-1 lg:min-h-[40px] lg:px-4 lg:py-2',
                disableSelectObject ? 'bg-[#DADADA]' : 'cursor-pointer bg-[#EFF4F5]'
              )}
            >
              {selectedObject ? (
                <span className="flex-1 text-left text-sm font-semibold">
                  {selectedObject.name ?? selectedObject.address?.address}
                </span>
              ) : (
                <span className="flex-1 text-left text-sm font-semibold">{t('select')}*</span>
              )}
            </div>
          </PopoverTrigger>
          {isMobileView ? <PopoverAnchor className="fixed top-0" /> : null}
          <PopoverContent
            className="max-h-screen w-screen overflow-hidden rounded-sm p-0 sm:max-w-[476px]"
            side={isMobileView ? 'bottom' : 'right'}
            sideOffset={isMobileView ? 0 : 10}
          >
            <ObjectRelationSettingsContent
              onChangeEstateId={onChangeEstateId}
              onChangeProjectId={onChangeProjectId}
              setSelectedObject={setSelectedObject}
              closePopover={() => setIsOpened(false)}
            />
          </PopoverContent>
        </Popover>
        {estateId || projectId ? (
          <Link
            to={estateId ? '/estates/$estateId' : '/projects/$projectId'}
            params={estateId ? { estateId } : { projectId }}
            className="absolute right-1 inline-flex h-8 w-8 items-center justify-center rounded-sm transition-colors hover:opacity-80 [&_svg]:size-5"
            target="_blank"
          >
            <ArrowUpRightIcon />
          </Link>
        ) : null}
      </div>
    </CollapsableCard>
  );
};
